import React, { useState, useEffect } from 'react';
import { Users, UtensilsCrossed, Mail, Phone } from 'lucide-react';
import PollHeaderBar from './PollHeaderBar';
import useSecondaryOf from '../utils/useSecondaryOf';
import SettingsModal from './SettingsModal';
import StatusToast from './StatusToast';

// (22 Sep 2026, Neil's decision) Dietary flags now show here too, not
// just on the live Phase 2 confirmation page (PollPage.jsx) — someone
// cooking near or after the event, checking who has an allergy, was
// previously left with nothing once the poll closed. DIETARY_FLAG_LABELS,
// MemberContactIcons and DietaryFlagsBanner below are copied verbatim from
// PollPage.jsx rather than shared/imported, matching the deliberate
// separation these two files already keep (see this file's own header
// comment on why it's a distinct component) — kept in sync by hand if
// either changes.
const DIETARY_FLAG_LABELS = {
  nut: { icon: '🥜', label: 'Nut' },
  gluten: { icon: '🌾', label: 'Gluten' },
  dairy: { icon: '🥛', label: 'Dairy' },
  egg: { icon: '🥚', label: 'Egg' },
  shellfish: { icon: '🦐', label: 'Shellfish' },
  garlic: { icon: '🧄', label: 'Garlic' },
  tomato: { icon: '🍅', label: 'Tomato' },
  vegan: { icon: '🌱', label: 'Vegan' },
  vegetarian: { icon: '🥕', label: 'Vegetarian' },
  // (29 Oct 2026, decision 156) Diets.
  dairy_free: { icon: '🥛', label: 'Dairy-free' },
  gluten_free: { icon: '🌾', label: 'Gluten-free' },
  pescatarian: { icon: '🐟', label: 'Pescatarian' },
  keto: { icon: '🥑', label: 'Keto' },
  other: { icon: '📝', label: 'Other' },
};

// (26 Oct 2026, decisions 146 and 147) Copied from PollPage.jsx, kept in sync by hand.
const HIDDEN_DISHES_NOTE = 'Dishes are a surprise until the day. The host will let you know if two offers clash.';
// The administrators see every dish; this reminds them that members don't yet.
const HIDDEN_DISHES_ADMIN_NOTE = "Members don't see these descriptions yet: the dishes are a surprise until the day.";
function ContainsLine({ allergens, allergensOther, myAllergies }) {
  const list = Array.isArray(allergens) ? allergens.filter((a) => DIETARY_FLAG_LABELS[a]) : [];
  if (!list.length && !allergensOther) return null;
  const mine = new Set(Array.isArray(myAllergies) ? myAllergies : []);
  const clash = list.some((a) => mine.has(a));
  const words = [...list.map((a) => DIETARY_FLAG_LABELS[a].label.toLowerCase()), ...(allergensOther ? [allergensOther] : [])];
  return (
    <span
      className={`block text-xs mt-0.5 ${clash ? 'w-fit rounded px-1.5 py-0.5 bg-red-50 text-red-700 font-semibold border border-red-200' : 'text-gray-500'}`}
    >
      {clash ? '⚠ ' : ''}Contains: {words.join(', ')}
      {clash ? ' (one of your allergies)' : ''}
    </span>
  );
}

// (23 Oct 2026, decision 124) The contact links only: since decision 124 the name sits
// inside the chip's brackets and these icons follow after the closing bracket.
function MemberContactIcons({ member }) {
  return (
    <React.Fragment>
      {member.email && (
        <a href={`mailto:${member.email}`} title={member.email} className="text-current">
          <Mail className="w-3 h-3 inline" />
        </a>
      )}
      {member.phone && (
        <a href={`tel:${member.phone}`} title={member.phone} className="text-current">
          <Phone className="w-3 h-3 inline" />
        </a>
      )}
    </React.Fragment>
  );
}

// (21 Oct 2026, 8.42, decision 119) Each chip reads "{item} ({Preferred Name})", e.g.
// "Vegetarian (Gary)". An "Other" flag shows the member's own words in place of "Other":
// "Alcohol & chocolate, Josie (Allan & Josie)".
// (23 Oct 2026, decision 124, amending 119) One chip per member, also when several members
// share an item ("Vegetarian (Gary)", "Vegetarian (Tim & Teri)"), so every icon clearly
// belongs to its name. The bracket holds the Preferred Name and, where an administrator
// added the flag, ", added by {name}"; the email and phone icons follow after the closing
// bracket: "Gluten (Turea & Allan) ✉". Kept the same in PollPage.jsx and PollResultsPage.jsx.
function DietaryChips({ flags, chipClass, addedClass }) {
  const chips = [];
  (flags || []).forEach((f) => {
    const icon = DIETARY_FLAG_LABELS[f.flag_type]?.icon || '📝';
    const label = DIETARY_FLAG_LABELS[f.flag_type]?.label || f.flag_type;
    (f.members || []).forEach((m, i) => {
      const text = f.flag_type === 'other' ? (String(m.other_text || '').trim() || label) : label;
      chips.push(
        <span key={`${f.flag_type}-${i}`} className={`inline-flex items-center gap-1 ${chipClass}`}>
          <span>
            {icon}{' '}{text}{' ('}{m.name}
            {m.enteredByName && (
              <span className={`italic ${addedClass}`}>, added by {m.enteredByName}</span>
            )}
            {')'}
          </span>
          <MemberContactIcons member={m} />
        </span>
      );
    });
  });
  return <>{chips}</>;
}

// Same component as PollPage.jsx's — a memory-jogger, never an assertion
// that a dish is safe. Visible on results at ANY poll status now (open,
// closed, mid-cycle), unlike PollPage.jsx's live version which is
// intentionally confirm-phase-only there (attendance isn't settled yet
// during offer phase, so it would always be empty at that point anyway).
function DietaryFlagsBanner({ dietaryFlags }) {
  const [expanded, setExpanded] = useState(true);
  if (!dietaryFlags || dietaryFlags.length === 0) return null;

  const allergies = dietaryFlags.filter((f) => f.severity === 'allergy');
  // (29 Oct 2026, decision 156) Diets, between Allergies and Aversions.
  const diets = dietaryFlags.filter((f) => f.severity === 'diet');
  const aversions = dietaryFlags.filter((f) => f.severity === 'aversion');

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 mb-3 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-3.5 py-2.5"
      >
        <span className="text-xs font-medium text-amber-900">
          Attending members have dietary needs
        </span>
        <span className="text-amber-700 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-3.5 pb-3.5">
          {allergies.length > 0 && (
            <div className="mb-2.5">
              <p className="text-[11px] font-semibold text-rose-900 tracking-tight mb-1.5">
                Allergies
              </p>
              <div className="flex flex-wrap gap-1.5">
                <DietaryChips
                  flags={allergies}
                  chipClass="text-[11px] bg-white border border-rose-200 text-rose-900 rounded-full px-2 py-1"
                  addedClass="text-rose-700"
                />
              </div>
            </div>
          )}

          {diets.length > 0 && (
            <div className="mb-2.5">
              <p className="text-[11px] font-semibold text-sky-900 tracking-tight mb-1.5">
                Diets
              </p>
              <div className="flex flex-wrap gap-1.5">
                <DietaryChips
                  flags={diets}
                  chipClass="text-[11px] bg-white border border-sky-200 text-sky-900 rounded-full px-2 py-1"
                  addedClass="text-sky-700"
                />
              </div>
            </div>
          )}

          {aversions.length > 0 && (
            <div className="mb-3">
              <p className="text-[11px] font-semibold text-green-800 tracking-tight mb-1.5">
                Aversions
              </p>
              <div className="flex flex-wrap gap-1.5">
                <DietaryChips
                  flags={aversions}
                  chipClass="text-[11px] bg-white border border-green-200 text-green-800 rounded-full px-2 py-1"
                  addedClass="text-green-700"
                />
              </div>
            </div>
          )}

          <p className="text-[11px] text-amber-800 leading-relaxed border-t border-amber-200 pt-2.5">
            A reminder, not a guarantee — check ingredients with attendees directly if you're unsure.
          </p>
        </div>
      )}
    </div>
  );
}

// (25 Sep 2026) Sort people by display name, ignoring case and accents.
function byName(people) {
  return [...people].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );
}

// (26 Oct 2026, decisions 142, 144) "✓ Confirmed", "✓ Confirmed by {administrator}",
// "not yet confirmed" (Phase 2 open) or "never confirmed" (Phase 2 closed).
function confirmationLabel(m, confirmMode) {
  if (m.confirmed !== false) return m.confirmedBy ? `✓ Confirmed by ${m.confirmedBy}` : '✓ Confirmed';
  return confirmMode === 'closed' ? 'never confirmed' : 'not yet confirmed';
}

// (24 Sep 2026) GA-only response breakdown (Grant's Apéro point 5). Every
// total is a button that opens the list of names behind it; Yes starts
// open. Not answered lists each active member still to reply, with an
// "Answer for them" button into proxy mode when the viewer may save on
// their behalf. Pending members (added, never signed in) are shown apart
// and aren't counted as non-responders — there's nothing to answer for
// them until they sign in.
// (26 Oct 2026, decisions 142, 144) confirmMode is 'open' or 'closed' for a Phase 2
// poll, else null. In Phase 2 each Yes and No row shows whether it is confirmed: a
// confirmed row is shaded green; a row carried over from Phase 1 stays plain and offers
// "Answer for them", so the administrator can confirm for a member who replied by
// WhatsApp. Phase 1 lists are unchanged.
function ResponseBreakdown({ roster, canAnswerForMembers, onAnswerForMember, confirmMode }) {
  const [open, setOpen] = useState('yes');
  const buckets = [
    { key: 'yes', label: 'Yes' },
    { key: 'no', label: 'No' },
    ...(roster.unsure.length > 0 ? [{ key: 'unsure', label: 'Not sure' }] : []),
    { key: 'notAnswered', label: 'Not answered' },
  ];
  // (25 Sep 2026) Alphabetical by name in every list, the same order as
  // the plain-member attendance list below, whatever order the API sends.
  const list = open ? byName(roster[open] || []) : [];
  const headcount = roster.yes.reduce((n, m) => n + (m.partySize || 1), 0);

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {buckets.map((b) => (
          <button
            key={b.key}
            type="button"
            onClick={() => setOpen(open === b.key ? null : b.key)}
            className={`text-xs rounded-full px-3 py-1.5 border ${
              open === b.key
                ? 'bg-indigo-600 border-indigo-600 text-white'
                : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            {b.label} {roster[b.key].length}
            {b.key === 'yes' && headcount !== roster.yes.length ? ` (${headcount} people)` : ''}
          </button>
        ))}
      </div>

      {open && (
        list.length > 0 ? (
          <ul className="space-y-1.5">
            {list.map((m) => {
              const showConfirmation = !!confirmMode && (open === 'yes' || open === 'no');
              const isConfirmed = m.confirmed !== false;
              const canAnswerHere = open === 'notAnswered' || (showConfirmation && !isConfirmed);
              return (
              <li
                key={m.memberId}
                className={`text-sm text-gray-700 flex items-center justify-between gap-2 ${
                  showConfirmation ? `rounded-lg px-2 py-1 ${isConfirmed ? 'bg-green-50' : ''}` : ''
                }`}
              >
                <span className="min-w-0">
                  {/* (26 Oct 2026) Name, party size and status each move to the next line
                      whole on a phone, never under the button. */}
                  <span className="whitespace-nowrap">{m.name}</span>
                  {open === 'yes' && m.partySize != null && <span className="text-gray-500 whitespace-nowrap"> (party of {m.partySize})</span>}
                  {open === 'unsure' && m.answer && m.answer !== 'Not sure' && <span className="text-gray-500"> ({m.answer})</span>}
                  {showConfirmation && (
                    <span className={`ml-1.5 text-xs inline-block whitespace-nowrap ${isConfirmed ? 'text-green-800 font-medium' : 'text-gray-500'}`}>
                      {confirmationLabel(m, confirmMode)}
                    </span>
                  )}
                </span>
                {canAnswerHere && canAnswerForMembers && onAnswerForMember && (
                  <button
                    type="button"
                    onClick={() => onAnswerForMember(m.memberId)}
                    className="text-xs text-indigo-700 border border-indigo-200 rounded-lg px-2 py-1 shrink-0"
                  >
                    Answer for them
                  </button>
                )}
              </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-400 italic">No one.</p>
        )
      )}

      {roster.pending.length > 0 && (
        <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
          Not yet signed in (not counted): {roster.pending.map((m) => m.name).join(', ')}
        </p>
      )}
    </div>
  );
}

// (Sep 2026) Read-only results view for the latest poll on an event — the
// counterpart to PollPage.jsx, which is for answering a currently-open
// poll, not viewing aggregate results. Before this existed, the "Poll
// closed · View responses" badge in MemberCalendarStep.jsx linked to
// PollPage anyway, which correctly (if unhelpfully) reported "There's no
// open poll for this event right now" once a poll closed — the label
// promised a feature that didn't exist. Deliberately a separate, simpler
// component rather than a "closed mode" bolted onto PollPage: that
// component's state (bulk response submission, offer-phase course tabs,
// editable inputs) doesn't apply here at all — this is pure display.
//
// (10 Sep 2026) Originally closed-only (see getPollResultsSummary's
// history in pollsService.js) — now also reachable from DashboardStep's
// new "View Results" link at ANY poll phase, per GA feedback that seeing
// current standing mid-cycle matters as much as seeing final numbers.
// Headers/wording below adapt based on isClosed rather than always
// reading as final.
//
// UPDATED 8 Sep 2026: header switched to the shared PollHeaderBar (see
// that file) — matches MemberCalendarStep.jsx's own header style, per
// Neil's decision to make this consistent with the landing page rather
// than each poll-related screen having its own separate visual language.
// Also gained a Settings/Sign Out escape hatch as a result, via the same
// SettingsModal usage pattern MemberCalendarStep.jsx and PollPage.jsx use.
const PollResultsPage = ({
  statusMessage,
  eventUid,
  apiBaseUrl,
  authToken,
  authenticatedFetch,
  formatEventDate,
  formatTime,
  getTzAbbr,
  userLocalTimezone,
  onBack,
  onStatusMessage,
  userEmail,
  preferredName,
  firstName,
  lastName,
  isAuthenticated,
  onSignOut,
  showSettings,
  setShowSettings,
  settingsInitialTab,
  setSettingsInitialTab,
  onSaveProfile,
  userTier,
  currentMemberCalendar,
  // (24 Sep 2026) Called with (calendarId, memberId, pollId) from the GA's
  // "Answer for them" button; App.js opens PollPage in proxy mode.
  onAnswerForMember,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const secondaryOf = useSecondaryOf(apiBaseUrl, authToken, results?.event?.calendarId);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const res = await authenticatedFetch(`${apiBaseUrl}/api/events/${eventUid}/polls/results`);
        const data = await res.json();
        if (res.ok) {
          setResults(data);
        } else {
          setError(data.error || 'Failed to load poll results');
        }
      } catch (err) {
        setError('Failed to load poll results');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [eventUid, apiBaseUrl, authenticatedFetch]);

  // (21 Sep 2026) Once the results are on screen, start at the top of the page: on a phone the
  // previous screen's scroll position could otherwise leave the first card under the sticky header.
  useEffect(() => {
    if (!isLoading) window.scrollTo(0, 0);
  }, [isLoading]);

  // (8 Sep 2026) Shared between the fallback and normal-render subtitles.
  // (13 Sep 2026) Time zone abbreviation folded in next to the time it
  // describes — same fix as PollPage.jsx's formatEventSubtitle, applied
  // here since this component builds its own subtitle independently
  // despite sharing PollHeaderBar with PollPage.
  const formatEventSubtitle = (evt) => {
    if (!evt) return undefined;
    const showTz = evt.timezone && evt.timezone !== userLocalTimezone;
    const tzAbbr = showTz
      ? (getTzAbbr ? (getTzAbbr(evt.timezone, evt.eventDate) || evt.timezone) : evt.timezone)
      : null;
    return (
      `${formatEventDate ? formatEventDate(evt.eventDate) : evt.eventDate}` +
      (evt.startTime
        ? ` · ${formatTime ? formatTime(evt.startTime) : evt.startTime}${tzAbbr ? ' ' + tzAbbr : ''}`
        : '') +
      (evt.location ? ` · ${evt.location}` : '')
    );
  };

  const settingsModal = showSettings && (
    <SettingsModal
      isOpen={showSettings}
      onClose={() => { setShowSettings(false); setSettingsInitialTab('profile'); }}
      userEmail={userEmail}
      apiBaseUrl={apiBaseUrl}
      authToken={authToken}
      onStatusMessage={onStatusMessage}
      userTier={userTier}
      initialTab={settingsInitialTab}
      currentCalendar={currentMemberCalendar}
      firstName={firstName}
      lastName={lastName}
      onSaveProfile={onSaveProfile}
      restrictToProfileTabs={true}
    />
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center" style={{ minHeight: '100dvh' }}>
        <StatusToast statusMessage={statusMessage} />
        <p className="text-sm text-gray-500">Loading results…</p>
      </div>
    );
  }

  if (error || !results) {
    // (8 Sep 2026, Next Action Points item 6) The real event title/date
    // genuinely aren't available here: unlike PollPage.jsx, this component
    // has only ONE fetch (polls/results), and if it fails there was never
    // a successful earlier fetch to fall back on. Generic title only.
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50" style={{ minHeight: '100dvh' }}>
        <StatusToast statusMessage={statusMessage} />
        <PollHeaderBar
          title="Poll results"
          userEmail={userEmail}
          preferredName={preferredName}
          firstName={firstName}
          lastName={lastName}
          isAuthenticated={isAuthenticated}
          onSignOut={onSignOut}
          onOpenSettings={() => setShowSettings(true)}
          onBack={onBack}
          secondaryOf={secondaryOf}
        />
        <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-gray-500">{error || "There's no open poll for this event right now."}</p>
        </div>
        {settingsModal}
      </div>
    );
  }

  const { poll, event, attendanceTotals, attendees, dishesByCourse: dishesByCourseRaw, dietaryFlags, cookingRequirements, roster, calendarId, canAnswerForMembers, dishesHidden, myAllergies } = results;
  // (26 Sep 2026, H6) Dishes alphabetical by dish within each course
  // (case-insensitive), instead of response order. Course order unchanged.
  // Sorted before requirementShownAt below, which relies on list position.
  // (26 Sep 2026) Courses in eating order too — Starter, Main, Dessert, then
  // Other (and any "Other (…)" variant), unknown names after, in their
  // original order. Same rule as PollPage.jsx's course tabs.
  const courseRank = (course) => {
    const c = String(course || '').trim().toLowerCase();
    if (c === 'starter') return 0;
    if (c === 'main') return 1;
    if (c === 'dessert') return 2;
    if (c.startsWith('other')) return 3;
    return 4;
  };
  const dishesByCourse = dishesByCourseRaw
    ? Object.fromEntries(Object.entries(dishesByCourseRaw)
      .map((entry, i) => ({ entry, i }))
      .sort((a, b) => (courseRank(a.entry[0]) - courseRank(b.entry[0])) || (a.i - b.i))
      .map(({ entry }) => entry)
      .map(([course, dishes]) => [
        course,
        [...(dishes || [])].sort((a, b) =>
          (a.description || '').localeCompare(b.description || '', 'en', { sensitivity: 'base' })
          || (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' })),
      ]))
    : dishesByCourseRaw;
  // (24 Sep 2026) Cooking requirements — shown once per member, under
  // their FIRST listed contribution (a member offering two dishes asked
  // for their requirement once, not per dish). A requirement with no dish
  // is not shown: it only means something alongside a dish, and
  // PollPage.jsx now clears it when a member clears their last dish.
  const requirementByMember = new Map((cookingRequirements || []).map((r) => [r.memberId, r.text]));
  const requirementShownAt = new Map(); // memberId -> "course|index" of the dish it appears under
  Object.entries(dishesByCourse || {}).forEach(([course, dishes]) => {
    dishes.forEach((d, i) => {
      if (requirementByMember.has(d.memberId) && !requirementShownAt.has(d.memberId)) {
        requirementShownAt.set(d.memberId, `${course}|${i}`);
      }
    });
  });

  // (25 Sep 2026) Alphabetical, matching the GA's breakdown lists (this
  // list came in response order before).
  const yesAttendees = byName(attendees.filter((a) => a.answer === 'Yes'));
  // (10 Sep 2026) This page now also renders live in-progress polls (see
  // getPollResultsSummary in pollsService.js) — "Final attendance" / "What
  // everyone brought" read as though the poll had already closed, which is
  // misleading for a still-open one a GA is checking mid-cycle.
  const isClosed = poll?.status === 'closed';
  // (3 Oct 2026) While the poll is open: not final, and in Phase 2 a Phase 1
  // Yes carried over (not yet confirmed) is listed separately.
  // (26 Oct 2026, decision 143) Also once Phase 2 has closed: a Yes never confirmed is
  // listed apart ("never confirmed") and left out of the final headcount.
  const isConfirmPhase = poll?.phase === 'confirm';
  const confirmMode = isConfirmPhase ? (isClosed ? 'closed' : 'open') : null;
  const carriedOver = isConfirmPhase ? yesAttendees.filter((a) => a.confirmed === false) : [];
  const confirmedYes = carriedOver.length ? yesAttendees.filter((a) => a.confirmed !== false) : yesAttendees;

  // (26 Oct 2026, decisions 142, 143) The headcount line. Phase 2 open: "9 confirmed
  // attending · 2 not yet confirmed · 1 declined"; Phase 2 closed: "9 attending · 2 never
  // confirmed · 1 declined"; Phase 1 as before. Falls back to the plain total if the
  // BackEnd has not yet sent the split.
  // Built as separate parts, each kept on one line on a phone.
  const attendanceParts = (() => {
    if (!attendanceTotals) return null;
    const declined = attendanceTotals.counts?.No ? [`${attendanceTotals.counts.No} declined`] : [];
    if (!confirmMode) return [`${attendanceTotals.headcount} attending`, ...declined];
    const confirmedCount = attendanceTotals.confirmedHeadcount ?? attendanceTotals.headcount;
    const unconfirmedCount = attendanceTotals.unconfirmedHeadcount ?? 0;
    if (confirmMode === 'open') {
      return [
        `${confirmedCount} confirmed attending`,
        ...(unconfirmedCount ? [`${unconfirmedCount} not yet confirmed`] : []),
        ...declined,
      ];
    }
    return [
      `${confirmedCount} attending`,
      ...(unconfirmedCount ? [`${unconfirmedCount} never confirmed`] : []),
      ...declined,
    ];
  })();

  return (
    // (21 Sep 2026) minHeight in dvh: a short page is exactly as tall as the VISIBLE screen, so
    // iPhone Safari has no spare height to scroll it by (100vh includes the space behind its
    // toolbars, which left a short page scrollable by a few dozen points).
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50" style={{ minHeight: '100dvh' }}>
      <StatusToast statusMessage={statusMessage} />
      <PollHeaderBar
        title={event.title}
        subtitle={formatEventSubtitle(event)}
        userEmail={userEmail}
        preferredName={preferredName}
        firstName={firstName}
        lastName={lastName}
        isAuthenticated={isAuthenticated}
        onSignOut={onSignOut}
        onOpenSettings={() => setShowSettings(true)}
        onBack={onBack}
        secondaryOf={secondaryOf}
      />

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <DietaryFlagsBanner dietaryFlags={dietaryFlags} />

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-800">{isClosed ? 'Final attendance' : 'Attendance so far — not final'}</h2>
          </div>
          {!isClosed && (
            <p className="text-xs text-gray-500 mb-2">
              Members can still answer or change their answer until the poll closes.
              {results.memberCount > 0 && ` ${results.answeredCount} of ${results.memberCount} members have ${isConfirmPhase ? 'confirmed' : 'answered'}.`}
            </p>
          )}
          {attendanceParts && (
            <p className="text-sm text-gray-600 mb-3">
              {attendanceParts.map((part, i) => (
                <React.Fragment key={part}>
                  {i > 0 && ' · '}
                  <span className="whitespace-nowrap">{part}</span>
                </React.Fragment>
              ))}
            </p>
          )}
          {roster ? (
            <ResponseBreakdown
              roster={roster}
              confirmMode={confirmMode}
              canAnswerForMembers={canAnswerForMembers}
              onAnswerForMember={onAnswerForMember && calendarId
                ? (memberId) => onAnswerForMember(calendarId, memberId, poll.id)
                : null}
            />
          ) : yesAttendees.length > 0 ? (
            <>
              <ul className="space-y-1">
                {confirmedYes.map((a) => (
                  <li key={a.memberId} className="text-sm text-gray-700">
                    {a.name}
                    {a.partySize != null && <span className="text-gray-500"> (party of {a.partySize})</span>}
                    {/* (26 Oct 2026, decision 144) */}
                    {isConfirmPhase && a.confirmedBy && <span className="text-xs text-gray-500 inline-block whitespace-nowrap">&nbsp;· confirmed by {a.confirmedBy}</span>}
                  </li>
                ))}
              </ul>
              {carriedOver.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-500 mt-3 mb-1">
                    {isClosed ? 'Said yes in Phase 1, never confirmed' : 'Said yes in Phase 1, not yet confirmed'}
                  </p>
                  <ul className="space-y-1">
                    {carriedOver.map((a) => (
                      <li key={a.memberId} className="text-sm text-gray-700">
                        {a.name}
                        {a.partySize != null && <span className="text-gray-500"> (party of {a.partySize})</span>}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400 italic">{isClosed ? 'No one confirmed as attending.' : 'No one has responded yet.'}</p>
          )}
        </div>

        {dishesByCourse && Object.keys(dishesByCourse).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <UtensilsCrossed className="w-4 h-4 text-gray-500" />
              <h2 className="text-sm font-semibold text-gray-800">{isClosed ? 'Member contributions' : 'What\'s been offered so far — not final'}</h2>
            </div>
            {Object.entries(dishesByCourse).map(([course, dishes]) => (
              <div key={course} className="mb-3 last:mb-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{course}</p>
                {dishes.length > 0 ? (
                  <ul className="space-y-1">
                    {dishes.map((d, i) => (
                      <li key={i} className="text-sm text-gray-700">
                        <span className="font-medium">{d.name}</span>
                        {/* (26 Oct 2026, decision 146) While dishes are a surprise. */}
                        {d.hidden ? <span className="text-gray-400 italic"> — a surprise</span> : <> — {d.description}</>}
                        {/* (26 Oct 2026, decision 144) A dish carried over from Phase 1 whose
                            member never confirmed in Phase 2. */}
                        {isConfirmPhase && isClosed && d.confirmed === false && (
                          <span className="text-gray-500"> (not confirmed)</span>
                        )}
                        <ContainsLine allergens={d.allergens} allergensOther={d.allergensOther} myAllergies={myAllergies} />
                        {requirementShownAt.get(d.memberId) === `${course}|${i}` && (
                          <p className="text-xs text-gray-500 mt-0.5 pl-3">
                            Cooking requirements: {requirementByMember.get(d.memberId)}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400 italic">Nothing offered for this course.</p>
                )}
              </div>
            ))}
            {dishesHidden && (
              <p className="text-xs text-gray-400 mt-2">{results.dishesHiddenFromMe ? HIDDEN_DISHES_NOTE : HIDDEN_DISHES_ADMIN_NOTE}</p>
            )}
          </div>
        )}
      </div>

      {settingsModal}
    </div>
  );
};

export default PollResultsPage;
